import React, { useState, useRef, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import BoroughDropdown from "../customHooks/boroughDropdown";
import WardDropdown from "../customHooks/wardDropdown";
import axiosInstance from "../../components/axiosInstance";
import { API_URLS } from "../../apiUrl";
import { MouzaRow } from "./MouzaListing";
import CustomSelect from "../../components/CustomSelect";

type FormData = {
  municipalityTbl_id: any;
  borough_tbl_id: any;
  ward_id: any;
  mouza_name: string;
  file: any;
};

interface Props {
  muniCipalityName: string;
  muniCipalityId: any;
  editData?: MouzaRow | null;
  isEditing?: boolean;
  onEditDone?: () => void;
}

const toastSuccess = (msg: string) =>
  toast.success(msg, { autoClose: 1500 });

const toastError = (msg: string) =>
  toast.error(msg, { autoClose: 1500 });

const toastWarning = (msg: string) =>
  toast.warning(msg, { autoClose: 1500 });

const AddMouza: React.FC<Props> = ({
  muniCipalityName,
  muniCipalityId,
  editData = null,
  isEditing = false,
  onEditDone,
}) => {
  const navigate = useNavigate();

  const buildInitialForm = (): FormData => ({
    municipalityTbl_id: muniCipalityId ?? editData?.municipality_id,
    borough_tbl_id: editData?.borough_primary_id
      ? String(editData.borough_primary_id)
      : editData?.borough_id
      ? String(editData.borough_id)
      : "",
    ward_id: editData?.ward_id ? String(editData.ward_id) : "",
    mouza_name: "",
    file: null,
  });

  const [formData, setFormData] = useState<FormData>(buildInitialForm);
  const [errors, setErrors] = useState<any>({});
  const [mouzaNames, setMouzaNames] = useState<string[]>([]);
  const [editMouzaName, setEditMouzaName] = useState<string>(
    editData?.mouza_name || ""
  );
  const [inputValue, setInputValue] = useState("");

  const formRef = useRef<HTMLFormElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [, , , boroughList] = BoroughDropdown(muniCipalityId);
  const [, , , wardList] = WardDropdown(
    muniCipalityId,
    formData.borough_tbl_id
  );

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | any
  ) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      if (!formData.borough_tbl_id) {
        toastWarning("Please select a Borough before choosing a file.");
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      if (!formData.ward_id) {
        toastWarning("Please select a Ward before choosing a file.");
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      setFormData((prev) => ({ ...prev, [name]: files ? files[0] : null }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const resetForm = () => {
    setFormData(buildInitialForm());
    setErrors({});
    setMouzaNames([]);
    setInputValue("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const validateForm = (): boolean => {
    const newErrors: any = {};
    if (!formData.borough_tbl_id)
      newErrors.borough_tbl_id = "Borough is required";
    if (!formData.ward_id) newErrors.ward_id = "Ward is required";
    if (isEditing) {
      if (!editMouzaName.trim())
        newErrors.mouza_name = "Mouza name is required";
    } else {
      if (mouzaNames.length === 0)
        newErrors.mouza_name = "Mouza name is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = { ...formData, mouza_name: mouzaNames.join("#") };

    try {
      const response = await axiosInstance.post(API_URLS.createmouza, payload);
      const resData = response.data?.data ?? response.data;

      const addedMouzas = resData?.addedMouzas;
      const duplicateMouzas = resData?.duplicateMouzas;

      if (Array.isArray(addedMouzas)) {
        if (addedMouzas.length > 0) {
          const names = addedMouzas.map((m: any) => m.mouza_name).join(", ");
          toastSuccess(`Mouza inserted successfully: ${names}`);
          resetForm();
          setTimeout(() => navigate("/dashboard/mouza-list"), 2100);
        } else if (duplicateMouzas?.length > 0) {
          toastWarning("Mouza already exists!");
        } else {
          toastError(response.data?.message || "Mouza add failed!");
        }
        return;
      }

      if (resData?.id || resData?.mouza_name) {
        const name = resData.mouza_name ?? "Mouza";
        toastSuccess(`Mouza "${name}" inserted successfully!`);
        resetForm();
        setTimeout(() => navigate("/dashboard/mouza-list"), 2100);
        return;
      }

      toastSuccess(response.data?.message || "Mouza inserted successfully!");
      resetForm();
      setTimeout(() => navigate("/dashboard/mouza-list"), 2100);
    } catch (error: any) {
      toastError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Mouza add failed."
      );
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      id: editData?.id,
      municipalityTbl_id: formData.municipalityTbl_id,
      borough_tbl_id: formData.borough_tbl_id,
      ward_id: formData.ward_id,
      mouza_name: editMouzaName.trim(),
    };

    try {
      const response = await axiosInstance.post(
        API_URLS.updatemouza,
        payload
      );
      toastSuccess(response.data?.message || "Mouza updated successfully!");
      setTimeout(() => {
        if (onEditDone) onEditDone();
      }, 1500);
    } catch (error: any) {
      toastError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Mouza update failed."
      );
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      const val = inputValue.trim();
      if (!mouzaNames.includes(val)) setMouzaNames([...mouzaNames, val]);
      setInputValue("");
      e.preventDefault();
    }
  };

  const removeTag = (name: string) =>
    setMouzaNames(mouzaNames.filter((n) => n !== name));

  const downloadSampleCSV = () => {
    const csvContent = [["mouza_name"], ["Mouza 001"]]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "mouza_bulk_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toastSuccess("Sample CSV downloaded!");
  };

  const handleCSVUpload = async () => {
    if (!formData.borough_tbl_id) {
      toastWarning("Please select a Borough before uploading.");
      return;
    }
    if (!formData.ward_id) {
      toastWarning("Please select a Ward before uploading.");
      return;
    }
    if (!formData.file) {
      toastWarning("Please select a file.");
      return;
    }

    const fd = new FormData();
    fd.append("file", formData.file);
    fd.append("municipalityTbl_id", String(muniCipalityId ?? ""));
    fd.append("borough_tbl_id", String(formData.borough_tbl_id ?? ""));
    fd.append("ward_id", String(formData.ward_id ?? ""));

    try {
      const response = await axiosInstance.post(
        API_URLS.BulkMouzaData,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      toastSuccess(response.data?.message || "CSV uploaded successfully!");
      setFormData((prev) => ({ ...prev, file: null }));
      if (fileInputRef.current) fileInputRef.current.value = "";
      setTimeout(() => navigate("/dashboard/mouza-list"), 1600);
    } catch (error: any) {
      toastError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "CSV upload failed."
      );
    }
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (formRef.current && !formRef.current.contains(e.target as Node))
        setErrors({});
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const inputCls =
    "bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500";

  const boroughOptions = (boroughList ?? []).map((b: any) => ({
    label: b.borough_name,
    value: String(b.borough_primary_id),
  }));

  const wardOptions = (wardList ?? []).map((w: any) => ({
    label: w.ward_no,
    value: String(w.ward_primary_id),
  }));

  return (
    <div className="max-w-full mx-auto">
      <ToastContainer position="top-right" />

      <div className="flex flex-col gap-5">
        <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
          {isEditing ? "Edit Mouza" : "Add Mouza"}
        </h1>

        <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-gray-800">
          <form
            ref={formRef}
            onSubmit={isEditing ? handleEditSubmit : handleSubmit}
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2">

              <div>
                <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Municipality <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={muniCipalityName || editData?.municipality_name || ""}
                  className={inputCls}
                  disabled
                />
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Borough <span className="text-red-600">*</span>
                </label>
                <CustomSelect
                  name="borough_tbl_id"
                  value={String(formData.borough_tbl_id)}
                  options={boroughOptions}
                  onChange={handleChange}
                  placeholder="Select Borough"
                />
                {errors.borough_tbl_id && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.borough_tbl_id}
                  </p>
                )}
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Ward <span className="text-red-600">*</span>
                </label>
                <CustomSelect
                  name="ward_id"
                  value={String(formData.ward_id)}
                  options={wardOptions}
                  onChange={handleChange}
                  placeholder="Select Ward"
                />
                {errors.ward_id && (
                  <p className="text-red-600 text-sm mt-1">{errors.ward_id}</p>
                )}
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Mouza Name <span className="text-red-600">*</span>
                  {!isEditing && (
                    <span className="text-red-500 text-xs font-medium ml-1">
                      (Type and press Enter)
                    </span>
                  )}
                </label>

                {isEditing ? (
                  <>
                    <input
                      type="text"
                      value={editMouzaName}
                      onChange={(e) => setEditMouzaName(e.target.value)}
                      className={inputCls}
                      placeholder="Mouza name"
                    />
                    {errors.mouza_name && (
                      <p className="text-red-600 text-sm mt-1">
                        {errors.mouza_name}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <div
                      className={`min-h-[42px] flex flex-wrap items-center gap-2 px-2.5 py-1.5 rounded-lg border text-sm
                        bg-gray-50 border-gray-300 text-gray-900
                        dark:bg-gray-700 dark:border-gray-600 dark:text-white
                        focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500`}
                    >
                      {mouzaNames.map((name, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
                        >
                          {name}
                          <button
                            type="button"
                            onClick={() => removeTag(name)}
                            className="text-blue-500 hover:text-red-500 text-base leading-none ml-0.5"
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                      <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyPress}
                        className="flex-1 min-w-[140px] bg-transparent outline-none placeholder-gray-400 dark:placeholder-gray-500 text-sm py-0.5"
                        placeholder={
                          mouzaNames.length === 0
                            ? "Type a mouza name and press Enter"
                            : "Add more…"
                        }
                      />
                    </div>
                    {errors.mouza_name && (
                      <p className="text-red-600 text-sm mt-1">
                        {errors.mouza_name}
                      </p>
                    )}
                  </>
                )}
              </div>

              {!isEditing && (
                <div className="lg:col-span-2 sm:col-span-2">
                  <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700/30">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">
                      Bulk Upload (CSV / Excel)
                    </p>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-wrap">
                      <input
                        ref={fileInputRef}
                        type="file"
                        id="file-upload"
                        name="file"
                        accept=".csv, .xls, .xlsx"
                        onChange={handleChange}
                        disabled={
                          !formData.borough_tbl_id || !formData.ward_id
                        }
                        title={
                          !formData.borough_tbl_id
                            ? "Please select a Borough first"
                            : !formData.ward_id
                            ? "Please select a Ward first"
                            : ""
                        }
                        className={`${inputCls} sm:w-auto flex-1 ${
                          !formData.borough_tbl_id || !formData.ward_id
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                      />
                      {formData.file && (
                        <span className="text-xs text-gray-600 dark:text-gray-400 truncate max-w-[180px]">
                          {formData.file.name}
                        </span>
                      )}
                    </div>
                    {(!formData.borough_tbl_id || !formData.ward_id) && (
                      <p className="text-xs text-orange-500 dark:text-orange-400 mt-2 font-medium">
                        ⚠️ Please select Borough and Ward first to enable file
                        upload.
                      </p>
                    )}
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      Select Borough &amp; Ward first, download the sample CSV,
                      fill in your mouza data, then upload.
                    </p>
                  </div>
                </div>
              )}

              <div className="lg:col-span-2 sm:col-span-2 mt-1">
                <div className="flex gap-2 flex-wrap">
                  {!isEditing && (
                    <>
                      <button
                        type="button"
                        onClick={downloadSampleCSV}
                        className="text-white bg-orange-500 hover:bg-orange-600 focus:ring-4 focus:outline-none focus:ring-orange-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center"
                      >
                        Download Sample CSV
                      </button>

                      <button
                        type="button"
                        onClick={handleCSVUpload}
                        disabled={!formData.file}
                        className="text-white bg-orange-500 hover:bg-orange-600 focus:ring-4 focus:outline-none focus:ring-orange-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center disabled:opacity-50"
                      >
                        Upload CSV
                      </button>
                    </>
                  )}

                  <button
                    type="submit"
                    disabled={!isEditing && formData.file != null}
                    className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 disabled:opacity-50"
                  >
                    {isEditing ? "Update" : "Submit"}
                  </button>

                  {isEditing && (
                    <button
                      type="button"
                      onClick={onEditDone}
                      className="text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>

            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddMouza;